import React, { createContext, useEffect, useState, useContext } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import loadable from "@loadable/component";
import { isValidNumber } from "libphonenumber-js";
import compact from "lodash/compact";
import sortBy from "lodash/sortBy";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { message } from "@/common/Message";
import { AccountContext } from "@/components/Account/Account";
import ConfigContext from "@/components/Config/configContext";
import { CREATE_OUTLET_MUTATION } from "@/graphql/mutations/createOutlet";
import { OUTLETS_QUERY } from "@/graphql/queries/getOutlets";
import { getHasEditPermissions } from "@/utils/accessControl";
import constants from "@/utils/constants";

const Button = loadable(() => import("@/common/Button"));
const Input = loadable(() => import("@/common/Input"));
const Layout = loadable(() => import("@/common/Layout"));
const OutletFormModal = loadable(() => import("./OutletFormModal"));
const OutletsTable = loadable(() => import("./OutletsTable"));
const Spin = loadable(() => import("@/common/Spin"));

export const OutletsContext = createContext({});
export const OutletsProvider = OutletsContext.Provider;

export default function Outlets() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const { configQuery } = useContext(ConfigContext);
  const { accountIsAdmin } = useContext(AccountContext);

  const hasEditPermissions = getHasEditPermissions();
  const isPermissionsEnabled = configQuery?.config?.features?.includes(
    constants.FEATURES.USE_PERMISSIONS_BASED_ACCESS,
  );
  const hasAccessToOutletEdit = isPermissionsEnabled
    ? configQuery?.config?.permissions?.settings?.outlets?.write
    : hasEditPermissions || accountIsAdmin;
  const hasAccessToOutletCreate = isPermissionsEnabled
    ? configQuery?.config?.permissions?.settings?.outlets?.create
    : hasEditPermissions;

  const [form, setForm] = useState({});
  const [outlets, setOutlets] = useState([]);
  const [createOutlet, createOutletResponse] = useMutation(
    CREATE_OUTLET_MUTATION,
    {
      context: { graph: "restaurants" },
      errorPolicy: "all",
      onCompleted: (data) => {
        if (data?.createOutlet) {
          setForm(data.createOutlet);
          message.success(t("outlets.form.success.create"));
          navigate(`/outlets/${data.createOutlet.id}/edit`);
        }
      },
      refetchQueries: [
        {
          query: OUTLETS_QUERY,
          context: { graph: "restaurants" },
        },
      ],
      // wait for refetch to complete before considering mutation complete
      awaitRefetchQueries: true,
    },
  );

  const outletsQuery = useQuery(OUTLETS_QUERY, {
    context: { graph: "restaurants" },
    onCompleted: (data) => {
      if (data?.outlets) {
        setOutlets(data.outlets);
      }
    },
  });

  useEffect(() => {
    if (outletsQuery?.error?.message) {
      message.error(outletsQuery?.error?.message);
    }
  }, [outletsQuery?.error]);

  function getFilteredData() {
    let filteredData = sortBy([...outlets], (outlet) =>
      outlet.archived ? 1 : 0,
    );
    if (searchTerm) {
      filteredData = filteredData.filter((outlet) =>
        compact([outlet.label, outlet.labelForPickup]).some((prop) =>
          prop.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }
    return filteredData;
  }

  return (
    <Layout className="outlets" pageTitle={t("outlets.header")}>
      <div className="flex w-full">
        <Input
          className="flex-grow mr-4"
          type="search"
          placeholder={t("common.search.placeholder")}
          leftIcon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {hasAccessToOutletCreate && (
          <Button type="primary" onClick={() => navigate("/outlets/new")}>
            {t("outlets.actions.add")}
          </Button>
        )}
      </div>
      <div className="mt-4">
        <OutletsTable
          outlets={getFilteredData()}
          editLink={
            hasAccessToOutletEdit
              ? (outlet) => `/outlets/${outlet.id}/edit`
              : null
          }
        />
      </div>
      <Spin spinning={outletsQuery?.loading}>
        <OutletFormModal
          form={form}
          setForm={setForm}
          onSave={() => {
            if (form.contactNumber && !isValidNumber(form.contactNumber)) {
              message.error(t("outlets.form.error.contactNumber"));
              return;
            }
            createOutlet({
              variables: {
                outlet: form,
              },
            });
          }}
          loading={createOutletResponse.loading}
          error={createOutletResponse.error}
        />
      </Spin>
    </Layout>
  );
}
